import React, { useState, useEffect } from 'react';
import { Card, InputNumber, Select, Button, Tag, Divider, Drawer } from 'antd';
import { motion } from 'framer-motion';
import { Calculator, Zap, Crown, Star, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SignupModal from './SignupModal';
import QuoteModal from './QuoteModal';
import { track } from '../analytics';

const PricingCalculator = () => {
  const [area, setArea] = useState(null);
  const [selectedAnalytics, setSelectedAnalytics] = useState([]);
  const [selectedCrops, setSelectedCrops] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [years, setYears] = useState(1);
  const [analyticsYears, setAnalyticsYears] = useState(1);
  const [totalCost, setTotalCost] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState('');
  const [signupOpen, setSignupOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { t } = useTranslation();

  const analyticsOptions = [
    { value: 'base', label: t('pricingCalc.base'), icon: <Zap size={16} /> },
    { value: 'advanced', label: t('pricingCalc.advanced'), icon: <Crown size={16} /> },
    { value: 'crop-intelligence', label: t('pricingCalc.cropIntelligence'), icon: <Star size={16} /> }
  ];

  const cropOptions = [
    { value: 'wheat', label: 'Wheat' },
    { value: 'corn', label: 'Corn' },
    { value: 'soybean', label: 'Soybean' },
    { value: 'barley', label: 'Barley' },
    { value: 'rice', label: 'Rice' },
    { value: 'cotton', label: 'Cotton' },
    { value: 'sunflower', label: 'Sunflower' },
    { value: 'canola', label: 'Canola' },
    { value: 'potato', label: 'Potato' }
  ];

  const regionOptions = [
    { value: 'europe', label: 'Europe' },
    { value: 'north-america', label: 'North America' },
    { value: 'south-america', label: 'South America' },
    { value: 'asia', label: 'Asia' },
    { value: 'africa', label: 'Africa' },
    { value: 'oceania', label: 'Oceania' }
  ];

  const getBasePlatformPrice = (hectares, year = 1) => {
    if (!hectares) return 0;
    
    let price = 0;
    if (year === 1) {
      // First year: first 150ha free, rest at tiered pricing
      const chargeableHectares = Math.max(hectares - 150, 0);
      if (chargeableHectares === 0) return 0;
      if (hectares < 1000) price = chargeableHectares * 3;
      else if (hectares < 5000) price = chargeableHectares * 2.5;
      else if (hectares < 20000) price = chargeableHectares * 2;
      else price = chargeableHectares * 1.5;
    } else {
      // Subsequent years: full area at tiered pricing
      if (hectares < 1000) price = hectares * 3;
      else if (hectares < 5000) price = hectares * 2.5;
      else if (hectares < 20000) price = hectares * 2;
      else price = hectares * 1.5;
    }
    return price;
  };

  const getAdvancedAnalyticsPrice = (hectares) => {
    if (!hectares) return 0;
    if (hectares < 1000) return hectares * 10;
    if (hectares < 5000) return hectares * 8;
    if (hectares < 20000) return hectares * 6.5;
    if (hectares < 50000) return hectares * 5.5;
    return hectares * 4.5;
  };

  const getCropIntelligencePrice = (cropCount) => {
    // Package-based pricing: 1-crop (€3,500), 3-crop (€9,000), 9-crop (€24,000)
    if (!cropCount || cropCount <= 0) return 0;
    const pkg1 = 3500;
    const pkg3 = 9000;
    const pkg9 = 24000;
    let remaining = cropCount;
    let total = 0;
    // Greedy optimal given unit costs: 9(2333) < 3(2666) < 1(3000)
    const n9 = Math.floor(remaining / 9);
    total += n9 * pkg9;
    remaining -= n9 * 9;
    const n3 = Math.floor(remaining / 3);
    total += n3 * pkg3;
    remaining -= n3 * 3;
    total += remaining * pkg1;
    return total;
  };

  const getCropIntelligenceDiscountAmount = (cropCount, years) => {
    if (years < 3 || !cropCount) return 0;
    let discountAmount = 0;
    const yearly = getCropIntelligencePrice(cropCount);
    for (let year = 2; year <= Math.min(years, 4); year++) {
      discountAmount += yearly * 0.2;
    }
    return discountAmount;
  };

  const getCropIntelligenceDiscountedTotal = (cropCount, years) => {
    const actual = (cropCount ? getCropIntelligencePrice(cropCount) : 0) * (years || 0);
    return actual - getCropIntelligenceDiscountAmount(cropCount, years);
  };

  const getCropIntelligencePackageBreakdown = (cropCount) => {
    if (!cropCount || cropCount <= 0) return [];
    let remaining = cropCount;
    const breakdown = [];
    const n9 = Math.floor(remaining / 9);
    if (n9 > 0) breakdown.push({ size: 9, count: n9 });
    remaining -= n9 * 9;
    const n3 = Math.floor(remaining / 3);
    if (n3 > 0) breakdown.push({ size: 3, count: n3 });
    remaining -= n3 * 3;
    const n1 = remaining;
    if (n1 > 0) breakdown.push({ size: 1, count: n1 });
    return breakdown;
  };

  const getCropIntelligencePackageUnitPrice = (size) => {
    if (size === 9) return 24000;
    if (size === 3) return 9000;
    return 3500;
  };

  const getCropIntelligencePackageLines = (cropCount, years) => {
    const packs = getCropIntelligencePackageBreakdown(cropCount);
    return packs.map(p => ({
      label: `${p.count > 1 ? `${p.count} × ` : ''}${p.size} crop${p.size > 1 ? 's' : ''} × ${years} year${years > 1 ? 's' : ''}`,
      amount: getCropIntelligencePackageUnitPrice(p.size) * years * p.count,
    }));
  };

  const getActualAnalyticsPrice = (hectares, years, isBasePlatform = false) => {
    if (!hectares) return 0;
    
    let totalCost = 0;
    
    for (let year = 1; year <= years; year++) {
      if (isBasePlatform) {
        totalCost += getBasePlatformPrice(hectares, year);
      } else {
        totalCost += getAdvancedAnalyticsPrice(hectares);
      }
    }
    
    return totalCost;
  };

  const getDiscountedAnalyticsPrice = (hectares, years, isBasePlatform = false) => {
    const actualPrice = getActualAnalyticsPrice(hectares, years, isBasePlatform);
    const discountAmount = getDiscountAmount(hectares, years, isBasePlatform);
    return actualPrice - discountAmount;
  };

  const getDiscountAmount = (hectares, years, isBasePlatform = false) => {
    if (years < 3 || !hectares) return 0;
    
    let discountAmount = 0;
    
    for (let year = 2; year <= Math.min(years, 4); year++) {
      let yearPrice;
      if (isBasePlatform) {
        yearPrice = getBasePlatformPrice(hectares, year);
      } else {
        yearPrice = getAdvancedAnalyticsPrice(hectares);
      }
      discountAmount += yearPrice * 0.2;
    }
    
    return discountAmount;
  };

  useEffect(() => {
    let cost = 0;

    if (selectedAnalytics.length > 0) {
      // Platform services depend on area
      if (area) {
        if (selectedAnalytics.includes('base')) {
          cost += getDiscountedAnalyticsPrice(area, analyticsYears, true);
        }
        if (selectedAnalytics.includes('advanced')) {
          cost += getDiscountedAnalyticsPrice(area, analyticsYears, false);
        }
      }

      // Crop Intelligence does not depend on area
      if (selectedAnalytics.includes('crop-intelligence') && selectedCrops.length > 0) {
        cost += getCropIntelligenceDiscountedTotal(selectedCrops.length, years);
      }
    }

    setTotalCost(cost);
  }, [area, selectedAnalytics, selectedCrops, years, analyticsYears]);

  const handleAnalyticsChange = (values) => {
    let coerced = Array.isArray(values) ? [...values] : [];
    if (coerced.includes('advanced') && !coerced.includes('base')) {
      coerced.push('base');
    }
    if (!coerced.includes('crop-intelligence')) {
      setSelectedCrops([]);
      setSelectedRegion(null);
      setYears(1);
    }
    setSelectedAnalytics(coerced);
  };

  const isCropIntelligenceSelected = selectedAnalytics.includes('crop-intelligence');

  const serviceInfo = {
    'platform-services': {
      title: 'Platform Services',
      content: (
        <div className="space-y-4">
          <p>Our core platform services provide the foundation for agricultural operations management.</p>
          <h4 className="font-semibold">Includes:</h4>
          <ul className="list-disc pl-5 space-y-2">
            <li>Real-time satellite monitoring</li>
            <li>Weather data integration</li>
            <li>Basic vegetation indices</li>
            <li>Field boundary management</li>
            <li>Mobile app access</li>
          </ul>
        </div>
      )
    },
    'base-platform': {
      title: 'Base Platform',
      content: (
        <div className="space-y-4">
          <p>Essential tools for modern agricultural operations with satellite-powered insights.</p>
          <h4 className="font-semibold">Features:</h4>
          <ul className="list-disc pl-5 space-y-2">
            <li>NDVI and vegetation health monitoring</li>
            <li>Weather forecasting and alerts</li>
            <li>Field mapping and zoning</li>
            <li>Basic reporting dashboard</li>
            <li>Email notifications</li>
          </ul>
          <p className="text-sm text-gray-600">Free for first 150 hectares, then tiered pricing based on area.</p>
        </div>
      )
    },
    'advanced-analytics': {
      title: 'Advanced Analytics',
      content: (
        <div className="space-y-4">
          <p>Premium analytics tools for precision agriculture and data-driven decision making.</p>
          <h4 className="font-semibold">Premium Features:</h4>
          <ul className="list-disc pl-5 space-y-2">
            <li>🌱 Virtual Tensiometer - Soil moisture prediction</li>
            <li>💧 Soil-Moisture Probe - Deep soil analysis</li>
            <li>🍃 Leaf N-Index - Nitrogen content monitoring</li>
            <li>🐛 Pest/Disease Early Warning - AI-powered alerts</li>
            <li>📊 Advanced reporting and analytics</li>
            <li>🔔 SMS and push notifications</li>
          </ul>
        </div>
      )
    },
    'crop-intelligence': {
      title: 'Crop Intelligence',
      content: (
        <div className="space-y-4">
          <p>Regional crop intelligence providing nationwide insights and benchmarking data.</p>
          <h4 className="font-semibold">Intelligence Features:</h4>
          <ul className="list-disc pl-5 space-y-2">
            <li>Regional crop performance benchmarking</li>
            <li>Market trend analysis and forecasting</li>
            <li>Yield prediction models</li>
            <li>Climate impact assessments</li>
            <li>Crop rotation recommendations</li>
            <li>Regional pest and disease tracking</li>
          </ul>
          <h4 className="font-semibold">Pricing Tiers:</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>1 crop: €2,500/year</li>
            <li>Up to 3 crops: €5,500/year</li>
            <li>Up to 9 crops: €12,000/year</li>
          </ul>
        </div>
      )
    }
  };

  const openDrawer = (serviceKey) => {
    setDrawerContent(serviceKey);
    setDrawerOpen(true);
  };

  return (
    <div className="py-16 bg-gradient-to-b from-light via-white to-light">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-6">
            <div className="p-3 bg-gradient-to-r from-primary to-green-600 rounded-xl text-white">
              <Calculator size={32} />
            </div>
          </div>
          <h3 className="text-4xl font-bold mb-4 text-dark">{t('pricingCalc.title')}</h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t('pricingCalc.subtitle')}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <Card className="p-8 rounded-3xl shadow-xl border-0 bg-gradient-to-br from-white to-gray-50">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column - Inputs */}
              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-3">{t('pricingCalc.analyticsServices')}</label>
                  <Select
                    mode="multiple"
                    size="large"
                    placeholder={t('pricingCalc.selectAnalyticsPlaceholder')}
                    value={selectedAnalytics}
                    onChange={handleAnalyticsChange}
                    className="w-full"
                    style={{ borderRadius: '12px' }}
                    optionLabelProp="label"
                  >
                    {analyticsOptions.map(option => (
                      <Select.Option key={option.value} value={option.value} label={option.label}>
                        <div className="flex items-center space-x-2">
                          {option.icon}
                          <span>{option.label}</span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {selectedAnalytics.includes('base') && (
                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-3">{t('pricingCalc.areaLabel')}</label>
                    <InputNumber
                      size="large"
                      placeholder={t('pricingCalc.areaPlaceholder')}
                      value={area}
                      onChange={(v) => { setArea(v); track('pricing_area_input', { area: v }); }}
                      min={1}
                      className="w-full"
                      style={{ borderRadius: '12px' }}
                    />
                  </div>
                )}

                {(selectedAnalytics.includes('base') || selectedAnalytics.includes('advanced')) && (
                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-3">{t('pricingCalc.analyticsYears')}</label>
                    <InputNumber
                      size="large"
                      placeholder={t('pricingCalc.yearsPlaceholder')}
                      value={analyticsYears}
                      onChange={(v) => { setAnalyticsYears(v); track('pricing_analytics_years', { years: v }); }}
                      min={1}
                      max={10}
                      className="w-full"
                      style={{ borderRadius: '12px' }}
                    />
                  </div>
                )}

                {isCropIntelligenceSelected && (
                  <>
                    <h4 className="text-2xl font-bold text-gray-800 mb-4">{t('pricingCalc.cropIntelligence')}</h4>
                    <div>
                      <label className="block text-lg font-semibold text-gray-700 mb-3">{t('pricingCalc.crops')}</label>
                      <Select
                        mode="multiple"
                        size="large"
                        placeholder={t('pricingCalc.selectCropsPlaceholder')}
                        value={selectedCrops}
                        onChange={(vals) => { setSelectedCrops(vals); track('pricing_crops_select', { count: vals.length }); }}
                        className="w-full"
                        style={{ borderRadius: '12px' }}
                      >
                        {cropOptions.map(option => (
                          <Select.Option key={option.value} value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>

                    <div>
                      <label className="block text-lg font-semibold text-gray-700 mb-3">{t('pricingCalc.region')}</label>
                      <Select
                        size="large"
                        placeholder={t('pricingCalc.selectRegionPlaceholder')}
                        value={selectedRegion}
                        onChange={setSelectedRegion}
                        className="w-full"
                        style={{ borderRadius: '12px' }}
                      >
                        {regionOptions.map(option => (
                          <Select.Option key={option.value} value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                      <p className="mt-2 text-sm text-gray-500">{t('common.regionCoverageNote')}</p>
                    </div>

                    <div>
                      <label className="block text-lg font-semibold text-gray-700 mb-3">{t('pricingCalc.years')}</label>
                      <InputNumber
                        size="large"
                        placeholder={t('pricingCalc.yearsPlaceholder')}
                        value={years}
                        onChange={(v) => { setYears(v); track('pricing_ci_years', { years: v }); }}
                        min={1}
                        max={10}
                        className="w-full"
                        style={{ borderRadius: '12px' }}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Right Column - Results */}
              <div className="bg-gradient-to-br from-primary/5 to-green-500/5 p-6 rounded-2xl">
                <h4 className="text-2xl font-bold text-gray-800 mb-6">{t('pricingCalc.priceBreakdown')}</h4>
                
                <div className="space-y-4">
                  {(selectedAnalytics.includes('base') || selectedAnalytics.includes('advanced')) && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-lg font-semibold text-gray-800">{t('pricingCalc.platformServices')}</h5>
                        <Info 
                          size={16} 
                          className="text-gray-500 hover:text-primary cursor-pointer" 
                          onClick={() => openDrawer('platform-services')}
                        />
                      </div>
                      {selectedAnalytics.includes('base') && (
                        <div className="flex justify-between items-center py-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-700">{t('pricingCalc.base')} × {analyticsYears} year{analyticsYears > 1 ? 's' : ''}</span>
                            <Info 
                              size={14} 
                              className="text-gray-400 hover:text-primary cursor-pointer" 
                              onClick={() => openDrawer('base-platform')}
                            />
                          </div>
                          <span className="font-semibold">
                            €{area ? getActualAnalyticsPrice(area, analyticsYears, true).toLocaleString() : 0}
                          </span>
                        </div>
                      )}
                      {selectedAnalytics.includes('advanced') && (
                        <div className="flex justify-between items-center py-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-700">{t('pricingCalc.advanced')} × {analyticsYears} year{analyticsYears > 1 ? 's' : ''}</span>
                            <Info 
                              size={14} 
                              className="text-gray-400 hover:text-primary cursor-pointer" 
                              onClick={() => openDrawer('advanced-analytics')}
                            />
                          </div>
                          <span className="font-semibold">
                            €{area ? getActualAnalyticsPrice(area, analyticsYears, false).toLocaleString() : 0}
                          </span>
                        </div>
                      )}
                      {analyticsYears >= 3 && area && (selectedAnalytics.includes('base') || selectedAnalytics.includes('advanced')) && (
                        <div className="flex justify-between items-center py-2 text-green-600">
                          <span className="text-sm">{t('common.threeYearIncentive')}</span>
                          <span className="font-semibold">
                            -€{(
                              (selectedAnalytics.includes('base') ? getDiscountAmount(area, analyticsYears, true) : 0) +
                              (selectedAnalytics.includes('advanced') ? getDiscountAmount(area, analyticsYears, false) : 0)
                            ).toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex justify-between items-center py-2">
                          <span className="font-semibold text-gray-800">{t('common.subtotal')}</span>
                          <span className="font-bold text-lg">
                            €{((selectedAnalytics.includes('base') ? (area ? getDiscountedAnalyticsPrice(area, analyticsYears, true) : 0) : 0) + (selectedAnalytics.includes('advanced') ? (area ? getDiscountedAnalyticsPrice(area, analyticsYears, false) : 0) : 0)).toLocaleString()}
                          </span>
                        </div>
                        {area && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">{t('common.costPerHectare')}</span>
                            <div className="text-sm font-bold text-primary">
                              €{((totalCost - (selectedAnalytics.includes('crop-intelligence') && selectedCrops.length > 0 ? getCropIntelligenceDiscountedTotal(selectedCrops.length, years) : 0)) / area / analyticsYears).toFixed(2)}/ha/year
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {selectedAnalytics.includes('crop-intelligence') && selectedCrops.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-lg font-semibold text-gray-800">{t('pricingCalc.cropIntelligence')}</h5>
                        <Info 
                          size={16} 
                          className="text-gray-500 hover:text-primary cursor-pointer" 
                          onClick={() => openDrawer('crop-intelligence')}
                        />
                      </div>
                      {(() => {
                        const packs = getCropIntelligencePackageBreakdown(selectedCrops.length);
                        return (
                          <div className="space-y-2">
                            {packs.map((p, idx) => (
                              <div className="flex justify-between items-center py-1" key={`${p.size}-${idx}`}>
                                <span className="text-gray-700">
                                  {p.count > 1 ? `${p.count} × ` : ''}{p.size} crop{p.size > 1 ? 's' : ''} × {years} year{years > 1 ? 's' : ''}
                                </span>
                                <span className="font-semibold">
                                  €{(getCropIntelligencePackageUnitPrice(p.size) * years * p.count).toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      {years >= 3 && (
                        <div className="flex justify-between items-center py-2 text-green-600">
                          <span className="text-sm">{t('common.threeYearIncentive')}</span>
                          <span className="font-semibold">
                            -€{getCropIntelligenceDiscountAmount(selectedCrops.length, years).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {selectedCrops.length > 0 && (
                        <div className="mt-3">
                          <div className="text-sm text-gray-600 mb-2">Selected Crops:</div>
                          <div className="flex flex-wrap gap-2">
                            {selectedCrops.map(crop => (
                              <Tag key={crop} color="green" className="rounded-full">
                                {cropOptions.find(c => c.value === crop)?.label}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {selectedAnalytics.length > 0 && (
                  <div className="mt-6 space-y-4">
                    <Divider className="my-4" />
                    <div className="flex justify-between items-center py-3 bg-gradient-to-r from-primary/10 to-green-500/10 px-4 rounded-xl">
                      <span className="text-xl font-bold text-gray-800">{t('common.total')}</span>
                      <span className="text-3xl font-black text-primary">
                        €{totalCost.toLocaleString()}
                      </span>
                    </div>
                    
                    <Button 
                      type="primary" 
                      size="large" 
                      className="w-full mt-6 h-12 rounded-xl font-semibold bg-gradient-to-r from-primary to-green-600 border-0"
                      onClick={() => {
                        if (totalCost >= 10000) { setQuoteOpen(true); track('pricing_get_quote_open', { total: totalCost, area: area || 0, analyticsYears, ciYears: years, cropsCount: selectedCrops.length, selectedAnalytics }); }
                        else { setSignupOpen(true); track('pricing_start_trial_open', { total: totalCost, area: area || 0 }); }
                      }}
                    >
                      {totalCost >= 50000 ? t('pricingCalc.letUsCallYou') : totalCost >= 10000 ? t('pricingCalc.getQuote') : t('common.startFreeTrial')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </motion.div>
        
        <Drawer
          title={serviceInfo[drawerContent]?.title}
          placement="right"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={400}
        >
          {serviceInfo[drawerContent]?.content}
        </Drawer>

        <SignupModal open={signupOpen} onClose={() => setSignupOpen(false)} quoteData={{
          area: area || 0,
          selectedAnalytics,
          analyticsYears,
          selectedCrops,
          years,
          selectedRegion,
          breakdown: (() => {
            const lines = [];
            let platformDiscount = 0;
            let platformSubtotal = 0;
            if (area && (selectedAnalytics.includes('base') || selectedAnalytics.includes('advanced'))) {
              if (selectedAnalytics.includes('base')) {
                const actual = getActualAnalyticsPrice(area, analyticsYears, true);
                platformSubtotal += actual;
                lines.push({ label: `Base Platform × ${analyticsYears} year${analyticsYears > 1 ? 's' : ''}`, amount: actual });
              }
              if (selectedAnalytics.includes('advanced')) {
                const actual = getActualAnalyticsPrice(area, analyticsYears, false);
                platformSubtotal += actual;
                lines.push({ label: `Advanced Analytics × ${analyticsYears} year${analyticsYears > 1 ? 's' : ''}`, amount: actual });
              }
              const disc = (selectedAnalytics.includes('base') ? getDiscountAmount(area, analyticsYears, true) : 0) + (selectedAnalytics.includes('advanced') ? getDiscountAmount(area, analyticsYears, false) : 0);
              platformDiscount = disc;
              if (disc > 0) {
                lines.push({ label: '3-year incentive (20% off)', amount: -disc });
              }
            }

            let ciDiscount = 0;
            let ciSubtotal = 0;
            if (selectedAnalytics.includes('crop-intelligence') && selectedCrops.length > 0) {
              const packLines = getCropIntelligencePackageLines(selectedCrops.length, years);
              packLines.forEach(pl => { lines.push(pl); ciSubtotal += pl.amount; });
              const disc = getCropIntelligenceDiscountAmount(selectedCrops.length, years);
              ciDiscount = disc;
              if (disc > 0) {
                lines.push({ label: '3-year incentive (20% off)', amount: -disc });
              }
            }

            const total = (platformSubtotal - platformDiscount) + (ciSubtotal - ciDiscount);
            return { lines, totals: { platformSubtotal, platformDiscount, ciSubtotal, ciDiscount, total } };
          })(),
          cropsText: selectedCrops.map(c => (cropOptions.find(x => x.value === c)?.label || c)).join(', '),
        }} />
        <QuoteModal 
          open={quoteOpen} 
          onClose={() => setQuoteOpen(false)} 
          defaultArea={area || undefined}
          quoteData={{
            area: area || 0,
            selectedAnalytics,
            analyticsYears,
            selectedCrops,
            years,
            selectedRegion,
            breakdown: (() => {
              const lines = [];
              let platformDiscount = 0;
              let platformSubtotal = 0;
              if (area && (selectedAnalytics.includes('base') || selectedAnalytics.includes('advanced'))) {
                if (selectedAnalytics.includes('base')) {
                  const actual = getActualAnalyticsPrice(area, analyticsYears, true);
                  platformSubtotal += actual;
                  lines.push({ label: `Base Platform × ${analyticsYears} year${analyticsYears > 1 ? 's' : ''}`, amount: actual });
                }
                if (selectedAnalytics.includes('advanced')) {
                  const actual = getActualAnalyticsPrice(area, analyticsYears, false);
                  platformSubtotal += actual;
                  lines.push({ label: `Advanced Analytics × ${analyticsYears} year${analyticsYears > 1 ? 's' : ''}`, amount: actual });
                }
                const disc = (selectedAnalytics.includes('base') ? getDiscountAmount(area, analyticsYears, true) : 0) + (selectedAnalytics.includes('advanced') ? getDiscountAmount(area, analyticsYears, false) : 0);
                platformDiscount = disc;
                if (disc > 0) {
                  lines.push({ label: '3-year incentive (20% off)', amount: -disc });
                }
              }

              let ciDiscount = 0;
              let ciSubtotal = 0;
              if (selectedAnalytics.includes('crop-intelligence') && selectedCrops.length > 0) {
                const packLines = getCropIntelligencePackageLines(selectedCrops.length, years);
                packLines.forEach(pl => { lines.push(pl); ciSubtotal += pl.amount; });
                const disc = getCropIntelligenceDiscountAmount(selectedCrops.length, years);
                ciDiscount = disc;
                if (disc > 0) {
                  lines.push({ label: '3-year incentive (20% off)', amount: -disc });
                }
              }

              const total = (platformSubtotal - platformDiscount) + (ciSubtotal - ciDiscount);
              return { lines, totals: { platformSubtotal, platformDiscount, ciSubtotal, ciDiscount, total } };
            })(),
            cropsText: selectedCrops.map(c => (cropOptions.find(x => x.value === c)?.label || c)).join(', '),
          }}
        />
      </div>
    </div>
  );
};

export default PricingCalculator;
